import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/react';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { isNonNullable, Opt } from 'app/tools/general.types';
import { makeArray, toStringPassNullable } from 'app/tools/utils';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { catchError, MonoTypeOperatorFunction, NEVER } from 'rxjs';
import {
  ApiError,
  isKeyedAxiosError,
} from '../api/api-core/api-core.types';

export enum ErrorType {
  PAGE_NOT_FOUND,
}

export const errorMsgMapping = (msg: string): string =>
  msg.search(/timeout of .* exceeded/i) !== -1
    ? 'Request timed out'
    : msg;

export const getErrorMsg = <T extends Opt<string | null>>(
  e: unknown,
  defaultMsg?: T
): T extends undefined ? string : string | T => {
  let errorMsg: string | null | undefined;
  if (isKeyedAxiosError(e)) errorMsg = e.data.detail;

  errorMsg =
    errorMsg ||
    toStringPassNullable(_.get(e, 'meta.error')) ||
    toStringPassNullable(_.get(e, 'error')) ||
    toStringPassNullable(_.get(e, 'message')) ||
    undefined;

  const rtn =
    defaultMsg === undefined
      ? errorMsg || 'Unknown error'
      : errorMsg || defaultMsg;

  // NOTE: Unfortunately this cannot be inferred by TypeScript
  return rtn as T extends undefined ? string : string | T;
};

export const logSentry = (err: unknown): void => {
  try {
    // NOTE: sets the error stack to here / where logSentry was called
    throw new Error(getErrorMsg(err));
  } catch (newErr: unknown) {
    if (_.isError(newErr) && _.isError(err)) {
      newErr.stack = [newErr.stack, err.stack]
        .filter(isNonNullable)
        .filter(s => s !== '')
        .join('\n\n');
    }

    let errData = {};

    if (!_.isObject(err)) {
      errData = { 'original error value': err };
    } else if (!_.isError(err)) {
      errData = err;
    } else {
      const { message, name, stack, ...other } = err;
      errData = other;
    }

    Sentry.captureException(newErr, {
      extra: errData,
    });
  }
};
export const logSentryOnNative = (err: unknown): void => {
  if (Capacitor.isNativePlatform()) logSentry(err);
};

export const silenceError = { error: (): void => {} };
export const silencePipeErrors =
  <T>(): MonoTypeOperatorFunction<T> =>
  in$ =>
    in$.pipe(catchError(() => NEVER));

export const useHandleFetchError = (e: Opt<ApiError>): void => {
  const { showSnack } = useShowInfo();
  const history = useHistory();
  const { pathname } = useLocation();
  const [erroredUrl, setErroredUrl] = useState('');
  if (e && erroredUrl !== pathname) {
    setErroredUrl(pathname);
    showSnack({
      message: e.detail || 'Error fetching entity',
      alertType: 'error',
    });
    history.goBack();
  }
};

/**
 * Merges multiple api errors into a single object for use with
 * the useInputForm hook.
 * @param errorsIn array of error objects for merging. Will also
 * take a single error object (not in an array) and just return
 * it as is, for backward compatibility.
 * @param baseObj object instance on which to build the merged
 * object. This will be emptied before use.
 * @returns the merged error object
 */
export const mergeApiErrors = (
  errorsIn?: ApiError | Opt<ApiError>[],
  baseObj = {}
): Opt<ApiError> => {
  if (!errorsIn) return;
  const errors = _.isArray(errorsIn) ? errorsIn : [errorsIn];
  const combinedError: ApiError = baseObj;
  _.keys(combinedError).forEach(k => delete combinedError[k]);

  errors.forEach(apiError =>
    _.entries(apiError).forEach(([key, newVal]) => {
      const currVal = combinedError[key];
      combinedError[key] = [currVal, newVal]
        .filter(isNonNullable)
        .join('<br>');
    })
  );
  return combinedError;
};

/**
 * Does exactly the same as mergeApiErrors, but instead is in
 * hook format to compare the array of errors by item and only
 * re-render if the individual array items change.
 */
export const useMergeApiErrors = (
  errs?: ApiError | Opt<ApiError>[]
): Opt<ApiError> => {
  const [baseObj, setBaseObj] = useState({});
  const prevErrsRef = useRef<typeof errs>();

  useEffect(() => {
    const errsArr = makeArray(errs);
    const prevErrsArr = makeArray(prevErrsRef.current);
    if (
      errsArr.length !== prevErrsArr.length ||
      errsArr.some((v, i) => v !== prevErrsArr[i])
    ) {
      setBaseObj({});
      prevErrsRef.current = errs;
    }
  }, [errs]);
  return mergeApiErrors(errs, baseObj);
};
